import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import NetworkMindMap from "../components/NetworkMindMap";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Sparkles, UserPlus, ArrowRight, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = "/api";

const PublicCirclePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPublic = async () => {
      try {
        const res = await fetch(`${API}/public/circle/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (res.ok) {
          setData(await res.json());
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPublic();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-6 text-center">
        <SEO title="Circle not found" description="This circle doesn't exist yet" noIndex />
        <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-violet-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "system-ui, sans-serif" }}>
          This circle doesn&apos;t exist
        </h1>
        <p className="text-slate-600 mb-6">The link might be broken or the user hasn&apos;t set up their circle yet.</p>
        <Button
          onClick={() => navigate("/welcome")}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
          data-testid="public-empty-cta"
        >
          Start your own circle
        </Button>
      </div>
    );
  }

  const { owner, nodes, stats } = data;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(ellipse at top, #f8f7ff 0%, #f1f0fb 40%, #eeedf7 100%)",
      }}
      data-testid="public-circle-page"
    >
      <SEO
        title={`${owner.first_name}'s Circle`}
        description={`${owner.first_name}'s growth circle on Evoluras — ${stats.total} honest voices. Build yours too.`}
        path={`/u/${slug}/circle`}
      />

      {/* Top bar */}
      <header className="max-w-5xl mx-auto px-5 sm:px-8 pt-6 pb-2 flex items-center justify-between">
        <Link to="/welcome" className="flex items-center gap-2 group" data-testid="public-logo-link">
          <img
            src="/evoluras-wordmark-mono.png"
            alt="Evoluras"
            className="h-6 sm:h-7 opacity-80 group-hover:opacity-100 transition-opacity"
          />
        </Link>
        <Button
          onClick={copyLink}
          variant="outline"
          size="sm"
          className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-50 text-xs"
          data-testid="public-copy-link"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
          Copy link
        </Button>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-4 pb-2 text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-100/70 text-violet-700 text-[11px] font-medium mb-4">
          <Sparkles className="w-3 h-3" />
          A growth circle on Evoluras
        </div>
        <div className="flex flex-col items-center mb-4">
          <div
            className="rounded-full overflow-hidden mb-3"
            style={{
              width: 80,
              height: 80,
              border: "3px solid #8b5cf6",
              boxShadow: "0 0 0 4px rgba(139,92,246,0.12), 0 4px 24px rgba(139,92,246,0.15)",
            }}
          >
            <Avatar className="w-full h-full">
              <AvatarImage src={owner.picture} alt={owner.name} />
              <AvatarFallback className="bg-violet-100 text-violet-600 text-xl font-bold">
                {owner.first_name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight"
            style={{ fontFamily: "system-ui, sans-serif" }}
            data-testid="public-owner-heading"
          >
            {owner.first_name}&apos;s Circle
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-lg">
            The people whose honest take shapes {owner.first_name}&apos;s journey —{" "}
            <span className="font-semibold text-slate-900">{stats.total}</span> voices
            {stats.with_insights > 0 && (
              <>, <span className="font-semibold text-slate-900">{stats.with_insights}</span> already exchanging insights</>
            )}
            .
          </p>
        </div>
      </section>

      {/* Orbital mindmap */}
      <section className="max-w-5xl mx-auto px-3 sm:px-8 pb-6">
        <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200/60">
          <NetworkMindMap externalData={data} readOnly />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-16 text-center">
        <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 p-8 sm:p-10 text-white shadow-xl shadow-violet-500/20">
          <h2
            className="text-2xl sm:text-3xl font-bold leading-tight mb-3"
            style={{ fontFamily: "system-ui, sans-serif" }}
          >
            Who&apos;d shape <em>your</em> year?
          </h2>
          <p className="text-violet-100 text-sm sm:text-base mb-6 max-w-md mx-auto">
            Build your own circle in 2 minutes. Get honest insights from the people
            who know you best — mentors, friends, family.
          </p>
          <Button
            onClick={() => navigate("/welcome")}
            size="lg"
            className="bg-white text-violet-700 hover:bg-violet-50 rounded-full px-8 shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="public-cta-start"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Start your circle
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-[11px] text-violet-200 mt-4">
            Takes 2 minutes
          </p>
        </div>

        <p className="text-[11px] text-slate-400 mt-8">
          Evoluras · Personal development that takes your people seriously
        </p>
      </section>
    </div>
  );
};

export default PublicCirclePage;
